import { Transition } from '@headlessui/react';
import { useCallback, useState } from 'react';
import { useBjForm } from '@hooks/useBjForm';
import { useTranslation } from 'next-i18next';
import { CardPaymentList } from '@components/features/payment';
import {
  Input, Button, Label, RadioText, GooglePlacesInput,
} from '@components/common/Forms';
import { PhoneInputStandalone } from '@components/common/Forms/PhoneInputStandalone/PhoneInputStandalone';
import { useRouterLocale } from '@hooks/common/useRouterLocale';
import { PencilIcon } from '@heroicons/react/24/solid';
import { clsx } from 'clsx';
import { MY_BESTIE_PRICE_EUR } from '../constants';

interface PaymentMethodsBestieProps {
  cartDetails?: ApiSchema<'UserOrderPayment'>;
  orderCredits: <Data, Args>(arg: Args) => Promise<Data | void>;
  savingOrder?: boolean;
  transitionProps?: object;
}

/**
 * @description Payment methods form for the My Bestie purchase.
 * Fixed at 50 neuro / 50 EUR — no amount input needed.
 */
export const PaymentMethodsBestie = (props: PaymentMethodsBestieProps) => {
  const {
    cartDetails, orderCredits, savingOrder, transitionProps,
  } = props;

  const locale = useRouterLocale();
  const { t } = useTranslation('common');
  const { record, handleCallbackSubmit } = useBjForm();

  const [selectedMethod, setSelectedMethod] = useState(cartDetails?.paymentTypes?.[0]);
  const [selectedCard, setSelectedCard] = useState(Number(cartDetails?.creditCards?.find(((card) => card.lastActive))?.id) || 0);
  const [phoneConfirmed, setPhoneConfirmed] = useState<boolean>(true);

  const isFormDefaultHidden = (
    (cartDetails?.lastName && cartDetails.lastName !== '')
    && (cartDetails?.firstName && cartDetails.firstName !== '')
    && (cartDetails?.address && cartDetails.address !== '')
    && (cartDetails.email && cartDetails.email !== '')
  ) as boolean;

  const [isFormClosed, setIsFormClosed] = useState<boolean>(isFormDefaultHidden);
  const classes = {
    label: 'font-bold text-ink-medium ml-3',
    parent: '!mt-0 mb-4',
  };

  const phoneConfirmationCallback = useCallback((confirmed: boolean) => {
    setPhoneConfirmed(confirmed);
  }, []);

  return (
    <form
      noValidate
      onSubmit={(event) => handleCallbackSubmit(event, orderCredits)}
    >
      <Transition
        as="div"
        className="px-6 pt-3"
        {...transitionProps}
      >
        <div className={clsx(
          'flex justify-between bg-surface-100 p-4',
          !isFormClosed && '!hidden',
        )}
        >
          <div>
            <span className="flex font-bold">
              {`${cartDetails?.firstName} ${cartDetails?.lastName}`}
            </span>
            <span className="flex">
              {cartDetails?.address}
            </span>
            <span className="flex">
              {cartDetails?.email}
            </span>
          </div>
          <div>
            <Button styling="text" color="ink-medium" onClick={() => setIsFormClosed(false)}>
              <PencilIcon className="size-6" />
            </Button>
          </div>
        </div>

        <div className={clsx(
          'md:grid md:grid-cols-2 md:gap-x-3',
          isFormClosed && '!hidden',
        )}
        >
          <div className={classes.parent}>
            <Label htmlFor="lastName" descriptionClass={classes.label} description={t('order.billing.lastName')} required>
              <Input defaultValue={cartDetails?.lastName || ''} type="text" {...record('lastName', {})} required />
            </Label>
          </div>
          <div className={classes.parent}>
            <Label htmlFor="firstName" descriptionClass={classes.label} description={t('order.billing.firstName')} required>
              <Input defaultValue={cartDetails?.firstName || ''} type="text" {...record('firstName', {})} required />
            </Label>
          </div>
          <div className={classes.parent}>
            <Label htmlFor="address" descriptionClass={classes.label} description={t('order.billing.address')} required>
              <GooglePlacesInput
                locationAddress={cartDetails?.address || ''}
                record={record}
                recordName="address"
              />
            </Label>
          </div>
          <div className={classes.parent}>
            <Label htmlFor="email" descriptionClass={classes.label} description={t('order.billing.email')} required>
              <Input defaultValue={cartDetails?.email || ''} type="text" {...record('email', {})} required />
            </Label>
          </div>
        </div>

        <div className={clsx(
          classes.parent,
          'hidden md:w-1/2 md:pr-3',
          !phoneConfirmed && '!block',
        )}
        >
          <PhoneInputStandalone
            toGetDefaultUserPhoneFromApi
            record={record}
            recordName="phone"
            locale={locale}
            phoneConfirmedStatusCallback={phoneConfirmationCallback}
          />
        </div>

        <Input type="hidden" value={selectedCard} {...record('creditCardId', { convertEmptyValue: 'toNumber' })} />
        <Input type="hidden" value={selectedMethod?.id} {...record('paymentTypeId')} />

        <div className="mt-4 border-t pt-4">
          <CardPaymentList
            paymentMethods={cartDetails?.paymentTypes}
            creditsAmount={MY_BESTIE_PRICE_EUR}
            creditCards={cartDetails?.creditCards}
            getSelectedMethod={setSelectedMethod}
            getSelectedCard={setSelectedCard}
            showAvailableCredits={false}
            className="max-w-sm md:!grid-cols-1"
          />
        </div>

        {selectedCard === 0 && (
          <div className="mt-3">
            <Label htmlFor="saveCard" className="flex">
              <Input type="checkbox" {...record('saveCard', { convertValue: 'toBoolean', defaultChecked: true })} />
              <RadioText>{t('payment.save.card.label')}</RadioText>
            </Label>
          </div>
        )}

        {/* Order summary — fixed price */}
        <div className="mt-3 border-y px-4 py-3">
          <div className="flex items-center justify-between py-1">
            <span>{t('mc.personal.agent')}</span>
            <strong className="text-right text-xl text-secondary">
              {MY_BESTIE_PRICE_EUR}
              <span className="ml-1 text-sm">
                &euro;&nbsp;
                {t('premium_free_trial_after_price.tva.text')}
              </span>
            </strong>
          </div>
        </div>
      </Transition>

      <div className="justify-center bg-surface px-6 pt-4 md:flex">
        <Button
          type="submit"
          rounding="full"
          color="secondary"
          size="sm"
          className="mb-4 w-full md:order-2 md:w-1/2"
          isLoading={savingOrder}
          disabled={!selectedMethod || !phoneConfirmed}
        >
          {t('mc.personal.agent.activate')
            .replace('{amount}', MY_BESTIE_PRICE_EUR.toString())}
        </Button>
      </div>
    </form>
  );
};
